/** @jsxImportSource react */
import { useEffect, useMemo, useState } from 'react'
import {
  Alert, Autocomplete, Badge, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton,
  MenuItem, Paper, Stack, TextField, Tooltip, Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import FilterListIcon from '@mui/icons-material/FilterList'
import DownloadIcon from '@mui/icons-material/Download'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'
import { reportsAPI } from '../../api/crm'
import { useEntityTypes, usePublishedForm, useTenantKey } from '../../hooks/useCrmQueries'
import { allFields } from '../../components/records/fieldValues'
import FilterBuilder from '../../components/records/FilterBuilder'
import { assignable, useMembers } from '../../components/records/PersonInput'
import { BarView } from '../../components/dashboard/WidgetViews'
import ReportTable from '../../components/reports/ReportTable'
import PageHeader from '../../components/common/PageHeader'
import Loading from '../../components/common/Loading'
import { downloadBlob, entityLabel, entityLabelPlural } from '../../utils/helpers'
import { getErrorMessage } from '../../utils/apiErrors'
import { useNotify } from '../../components/common/NotificationProvider'

const GROUPABLE = ['SELECT', 'RADIO', 'MULTI_SELECT', 'CHECKBOX', 'LOOKUP', 'USER', 'TEXT', 'EMAIL', 'PHONE', 'URL']
const DATES = ['DATE', 'DATETIME']
const NUMERIC = ['NUMBER', 'DECIMAL', 'CURRENCY', 'ROLLUP']
const INTERVALS = [['DAY', 'Day'], ['WEEK', 'Week'], ['MONTH', 'Month'], ['QUARTER', 'Quarter'], ['YEAR', 'Year']]
const FNS = [['SUM', 'Total'], ['AVG', 'Average'], ['MIN', 'Lowest'], ['MAX', 'Highest']]
const EMPTY = { filters: {}, groupBy: null, splitBy: null, measures: [{ fn: 'COUNT' }], sort: 'group', descending: false }

/** One grouping: a field (dates also pick an interval). */
const GroupPicker = ({ label, value, onChange, options, isDate }) => (
  <Stack direction="row" spacing={1} sx={{ flex: 1, minWidth: 0 }}>
    <TextField select size="small" label={label} value={value?.field || ''} sx={{ flex: 2, minWidth: 140 }}
      onChange={(e) => onChange(e.target.value ? { field: e.target.value, interval: isDate(e.target.value) ? (value?.interval || 'MONTH') : null } : null)}>
      <MenuItem value=""><em>None</em></MenuItem>
      {options.map((o) => <MenuItem key={o.key} value={o.key}>{o.label}</MenuItem>)}
    </TextField>
    {value && isDate(value.field) && (
      <TextField select size="small" label="By" value={value.interval || 'MONTH'} sx={{ flex: 1, minWidth: 100 }}
        onChange={(e) => onChange({ ...value, interval: e.target.value })}>
        {INTERVALS.map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
      </TextField>
    )}
  </Stack>
)

const ReportBuilderPage = () => {
  const { reportId } = useParams()
  const isNew = !reportId
  const navigate = useNavigate()
  const notify = useNotify()
  const tenantId = useTenantKey()
  const queryClient = useQueryClient()
  const { entityTypes, forms } = useEntityTypes()
  const saved = useQuery({ queryKey: [tenantId, 'reports', reportId], queryFn: () => reportsAPI.get(reportId), enabled: !isNew })
  const [entityType, setEntityType] = useState('')
  const [def, setDef] = useState(EMPTY)
  const [filterAnchor, setFilterAnchor] = useState(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (saved.data) {
      setEntityType(saved.data.entityType)
      setDef({ ...EMPTY, ...saved.data.definition })
    }
  }, [saved.data])
  useEffect(() => {
    if (isNew && !entityType && entityTypes.length) setEntityType(entityTypes[0])
  }, [isNew, entityType, entityTypes])

  const code = forms.find((f) => f.entityType === entityType)?.code
  const { data: form } = usePublishedForm(code)
  const fields = useMemo(() => allFields(form), [form])
  const groupOptions = useMemo(() => [
    ...fields.filter((f) => GROUPABLE.includes(f.fieldType) || DATES.includes(f.fieldType)).map((f) => ({ key: f.fieldKey, label: f.label })),
    { key: 'createdOn', label: 'Created' }, { key: 'updatedOn', label: 'Last updated' },
  ], [fields])
  const numberFields = fields.filter((f) => NUMERIC.includes(f.fieldType))
  const isDate = (key) => key === 'createdOn' || key === 'updatedOn' || DATES.includes(fields.find((f) => f.fieldKey === key)?.fieldType)

  const definition = { entityType, ...def }
  const ready = entityType && fields.length > 0 && def.measures.every((m) => m.fn === 'COUNT' || m.field)
  const result = useQuery({
    queryKey: [tenantId, 'reports', 'run', definition],
    queryFn: () => reportsAPI.run(definition),
    enabled: !!ready,
    placeholderData: (prev) => prev,
  })
  const canEdit = isNew || saved.data?.canEdit
  const set = (patch) => setDef((d) => ({ ...d, ...patch }))
  const setMeasure = (i, m) => set({ measures: def.measures.map((x, j) => (j === i ? m : x)) })
  const filterCount = Object.values(def.filters || {}).reduce((n, ops) => n + Object.keys(ops).length, 0)

  const exportCsv = useMutation({
    mutationFn: () => reportsAPI.runCsv(definition),
    onSuccess: (b) => downloadBlob(b, `${saved.data?.name || entityLabelPlural(entityType)}.csv`),
    onError: (e) => notify(getErrorMessage(e), 'error'),
  })
  const remove = useMutation({
    mutationFn: () => reportsAPI.remove(reportId),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: [tenantId, 'reports'] }); notify('Report deleted'); navigate('/reports') },
  })

  if (!isNew && saved.isLoading) return <Loading />
  if (saved.error) return <Alert severity="error">{getErrorMessage(saved.error)}</Alert>
  const r = result.data
  const chart = r && r.rows.length > 0 && r.columns.filter((c) => !c.measure).length === 1 && r.rows.length <= 40
    ? { series: r.rows.map((row) => ({ label: row.groups[0].label, value: row.values[0] })), measureLabel: r.columns.find((c) => c.measure).label,
        measureType: r.columns.find((c) => c.measure).fieldType }
    : null

  return (
    <Box>
      <PageHeader title={saved.data?.name || 'New report'} subtitle={isNew ? 'Group your records and add up what matters.' : `${entityLabelPlural(entityType)}${saved.data.shared ? ' · shared with the workspace' : ''}`}
        actions={(
          <Stack direction="row" spacing={1}>
            <Button startIcon={<DownloadIcon />} disabled={!ready || exportCsv.isPending} onClick={() => exportCsv.mutate()}>CSV</Button>
            {!isNew && canEdit && <Button color="error" onClick={() => remove.mutate()} disabled={remove.isPending}>Delete</Button>}
            <Button variant="contained" disabled={!ready} onClick={() => setSaving(true)}>{isNew || !canEdit ? 'Save report' : 'Save changes'}</Button>
          </Stack>
        )} />

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
            <TextField select size="small" label="Records" value={entityType} sx={{ minWidth: 180 , width: '70%'}} disabled={!isNew}
              onChange={(e) => { setEntityType(e.target.value); setDef(EMPTY) }}>
              {entityTypes.map((t) => <MenuItem key={t} value={t}>{entityLabelPlural(t)}</MenuItem>)}
            </TextField>
            <GroupPicker label="Group by" value={def.groupBy} options={groupOptions} isDate={isDate}
              onChange={(g) => set({ groupBy: g, splitBy: g ? def.splitBy : null })} />
            {def.groupBy && <GroupPicker label="Then by" value={def.splitBy} options={groupOptions.filter((o) => o.key !== def.groupBy.field)} isDate={isDate} onChange={(g) => set({ splitBy: g })} />}
            <Badge badgeContent={filterCount} color="primary">
              <Button variant="outlined" startIcon={<FilterListIcon />} onClick={(e) => setFilterAnchor(e.currentTarget)}>Filters</Button>
            </Badge>
          </Stack>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1.5 }} alignItems="center">
            <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>Show</Typography>
            {def.measures.map((m, i) => (
              <Stack key={i} direction="row" spacing={0.5} alignItems="center" sx={{ border: 1, borderColor: 'divider', borderRadius: 1, pl: 1 }}>
                <TextField select size="small" variant="standard" value={m.fn} InputProps={{ disableUnderline: true }}
                  onChange={(e) => setMeasure(i, e.target.value === 'COUNT' ? { fn: 'COUNT' } : { fn: e.target.value, field: m.field || numberFields[0]?.fieldKey })}>
                  <MenuItem value="COUNT">Count</MenuItem>
                  {numberFields.length > 0 && FNS.map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
                </TextField>
                {m.fn !== 'COUNT' && (
                  <TextField select size="small" variant="standard" value={m.field || ''} InputProps={{ disableUnderline: true }} onChange={(e) => setMeasure(i, { ...m, field: e.target.value })}>
                    {numberFields.map((f) => <MenuItem key={f.fieldKey} value={f.fieldKey}>{f.label.toLowerCase()}</MenuItem>)}
                  </TextField>
                )}
                {def.measures.length > 1 && (
                  <IconButton size="small" aria-label="Remove" onClick={() => set({ measures: def.measures.filter((_, j) => j !== i), sort: 'group' })}><CloseIcon fontSize="small" /></IconButton>
                )}
              </Stack>
            ))}
            {def.measures.length < 6 && (
              <Button size="small" startIcon={<AddIcon />} onClick={() => set({ measures: [...def.measures, numberFields[0] ? { fn: 'SUM', field: numberFields[0].fieldKey } : { fn: 'COUNT' }] })}>Add</Button>
            )}
            <Box sx={{ flexGrow: 1 }} />
            <TextField select size="small" label="Sort by" value={def.sort || 'group'} sx={{ minWidth: 160 }} onChange={(e) => set({ sort: e.target.value })}>
              <MenuItem value="group">Grouping</MenuItem>
              {def.measures.map((m, i) => <MenuItem key={i} value={`m${i}`}>{r?.columns.filter((c) => c.measure)[i]?.label || `Measure ${i + 1}`}</MenuItem>)}
            </TextField>
            <FormControlLabel control={<Checkbox size="small" checked={!!def.descending} onChange={(e) => set({ descending: e.target.checked })} />} label="Descending" />
          </Stack>
        </Stack>
      </Paper>

      {result.error && <Alert severity="error" sx={{ mb: 2 }}>{getErrorMessage(result.error)}</Alert>}
      {!r && result.isFetching && <Loading />}
      {r && (
        <Stack direction={{ xs: 'column', lg: chart ? 'row' : 'column' }} spacing={2} alignItems="flex-start">
          <Paper variant="outlined" sx={{ flex: 1, minWidth: 0, width: '100%', opacity: result.isFetching ? 0.6 : 1 }}>
            {r.rows.length === 0 && r.columns.some((c) => !c.measure)
              ? <Typography color="text.secondary" sx={{ p: 2 }}>No {entityLabelPlural(entityType).toLowerCase()} match.</Typography>
              : <ReportTable result={r} />}
          </Paper>
          {chart && (
            <Paper variant="outlined" sx={{ p: 2, width: { xs: '100%', lg: 420 }, flexShrink: 0 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>{chart.measureLabel} by {r.columns[0].label.toLowerCase()}</Typography>
              <BarView data={chart} />
            </Paper>
          )}
        </Stack>
      )}

      {filterAnchor && (
        <FilterBuilder anchorEl={filterAnchor} open onClose={() => setFilterAnchor(null)} fields={fields} filters={def.filters || {}}
          onApply={(filters) => set({ filters })} />
      )}
      {saving && (
        <SaveDialog report={canEdit ? saved.data : null} definition={definition} onClose={() => setSaving(false)}
          onSaved={(rep) => { setSaving(false); if (isNew || !canEdit) navigate(`/reports/${rep.id}`, { replace: true }) }} />
      )}
    </Box>
  )
}

/** Name, sharing and the email schedule. */
const SaveDialog = ({ report, definition, onClose, onSaved }) => {
  const tenantId = useTenantKey()
  const queryClient = useQueryClient()
  const notify = useNotify()
  const { data: members = [] } = useMembers()
  const [name, setName] = useState(report?.name || `${entityLabel(definition.entityType)} report`)
  const [shared, setShared] = useState(report?.shared || false)
  const [schedule, setSchedule] = useState(report?.schedule || 'NONE')
  const [recipients, setRecipients] = useState(report?.recipientIds || [])
  const save = useMutation({
    mutationFn: () => {
      const body = { name: name.trim(), definition, shared, schedule, recipientIds: shared ? recipients : [] }
      return report ? reportsAPI.update(report.id, body) : reportsAPI.create(body)
    },
    onSuccess: (rep) => { queryClient.invalidateQueries({ queryKey: [tenantId, 'reports'] }); notify('Report saved'); onSaved(rep) },
  })
  const options = assignable(members).map((m) => m.userId)
  const label = (id) => { const m = members.find((x) => x.userId === id); return m?.name || m?.email || 'Member' }
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{report ? 'Save report' : 'Save as a report'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {save.error && <Alert severity="error">{getErrorMessage(save.error)}</Alert>}
          <TextField label="Name" required autoFocus value={name} onChange={(e) => setName(e.target.value)} inputProps={{ maxLength: 200 }} />
          <FormControlLabel control={<Checkbox checked={shared} onChange={(e) => setShared(e.target.checked)} />}
            label={<Typography variant="body2">Share with the workspace <Typography component="span" variant="caption" color="text.secondary">(each person sees only records they can see)</Typography></Typography>} />
          <TextField select label="Email it" value={schedule} onChange={(e) => setSchedule(e.target.value)}>
            <MenuItem value="NONE">Don’t email</MenuItem>
            <MenuItem value="WEEKLY">Every Monday morning</MenuItem>
            <MenuItem value="MONTHLY">On the 1st of each month</MenuItem>
          </TextField>
          {schedule !== 'NONE' && (shared ? (
            <Autocomplete multiple options={options} value={recipients} getOptionLabel={label} onChange={(_, v) => setRecipients(v)}
              renderInput={(p) => <TextField {...p} label="Also to" placeholder="Colleagues" helperText="You always get it" />} />
          ) : (
            <Tooltip title="Share the report to email it to colleagues"><Typography variant="caption" color="text.secondary">Emailed to you.</Typography></Tooltip>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!name.trim() || save.isPending} onClick={() => save.mutate()}>Save</Button>
      </DialogActions>
    </Dialog>
  )
}

export default ReportBuilderPage
